import { useQuery } from "@apollo/client";
import { CheckIcon } from "@heroicons/react/24/solid";
import { useCallback, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

import {
  multiSelect,
  SelectList,
  Table,
} from "../../../../components/Functional";
import { Avatar } from "../../../../components/ui";
import HeaderComponent from "../../../../components/ui/ListHeader";
import { INVOICE_TYPE } from "../../../../constants/invoiceTypes";
import { FETCH_LISTS as fetchLists } from "../../../../graphql/query/List";
import { updateSearchResultNavigationStore } from "../../../../util/searchResultNavigation";
import {
  invoiceFilterStore,
  invoiceNavigationStore,
} from "../../components/useInvoiceFilters";
import { buildInvoiceNavigationTargetUrl } from "../../components/searchResultNavigation";
import Actions from "./components/Actions";
import searchedInvoices from "./components/SearchedInvoices";
import * as SelectListSaved from "./components/SelectListSaved";
import * as SelectListWorking from "./components/SelectListWorking";

const InvoiceNumber = props => {
  const row = props?.values?.item || props?.values || {};
  const { id, type = {}, company = {}, number, proforma } = row;

  const reference =
    type?.id === INVOICE_TYPE.INVOICE
      ? `${company.invoiceCode || ""}${number}`
      : `${proforma}`;

  if (typeof id !== "number") {
    return <p>{reference}</p>;
  }

  // Mirrors the Actions column href so query state (status, listId) is
  // preserved when the user navigates to the detail page from the list.
  const query = new URLSearchParams(window.location.search);
  const href = buildInvoiceNavigationTargetUrl({
    listContext: { locationSearch: window.location.search },
    recordId: id,
    status: query.get("status") || "all",
  });

  return (
    <Link to={href} className="underline hover:no-underline">
      {reference}
    </Link>
  );
};

const nameAndCompany = props => {
  const { contact, companyName } = props?.values?.item || props?.values || {};
  return (
    <>
      <div className="flex items-center gap-2">
        <Avatar
          size="lg"
          src={contact?.img}
          label={contact?.img || contact?.name}
        />
        <div>
          <p>{contact?.name}</p>
          <p className="mt-1 italic text-gray-400">{companyName}</p>
        </div>
      </div>
    </>
  );
};

const Approved = props => {
  // show check icon if value is -1
  return (
    <div className="flex items-center justify-center">
      {(props?.values?.item?.approved === -1 ||
        props?.values?.approved === -1) && (
        <CheckIcon className="h-5 w-5" aria-hidden="true" />
      )}
    </div>
  );
};

const List = props => {
  const { formik } = props;
  const navigate = useNavigate();
  const location = useLocation();
  const { data: { lists: { edges = [] } = {} } = {} } = useQuery(fetchLists(), {
    variables: {
      // fetch invoice_working list
      input: { typeId: 10 },
    },
    fetchPolicy: "network-only",
  });
  const workingListId = edges?.length > 0 ? edges[0]?.id : null;

  const queryParams = new URLSearchParams(window.location.search);
  const status = queryParams?.get("status") || "all";
  const listId = +queryParams.get("listId");

  const ids = Object.keys(formik?.values?.selected?.ids || {})?.map(item =>
    parseInt(item.replace("_", "")),
  );

  useEffect(() => {
    if (workingListId) {
      queryParams.set("workingListId", workingListId);
      const newUrl = `${window.location.pathname}?${queryParams.toString()}`;
      // eslint-disable-next-line
      history.pushState(null, null, newUrl);
    }
  }, [workingListId]);

  const multiSelectProps = {
    formik,
    label: "Invoices",
    name: "selected",
    keyName: "id",
  };

  const actionProps = {
    formik,
    ids,
    listId,
    onSuccess: () => formik?.resetForm(),
    search: invoiceFilterStore.get(),
    workingListId,
  };

  const captureNavigationSnapshot = useCallback(
    ({ count, edges, queryVariables }) => {
      if (status !== "all") return;

      updateSearchResultNavigationStore(invoiceNavigationStore, {
        count,
        edges,
        listContext: { locationSearch: location.search },
        queryVariables,
        statusContext: status,
      });
    },
    [location.search, status],
  );

  const handleChange = value => {
    formik?.resetForm();
    let url = `/invoice?status=${value}`;

    // For working list tabs, use listId if present (saved list), otherwise use workingListId
    if (value === "selectListWorking" || value === "selectListSaved") {
      // Include listId if present (viewing/editing a saved list), otherwise use workingListId
      if (listId) {
        url += `&listId=${listId}`;
      } else if (workingListId) {
        url += `&workingListId=${workingListId}`;
      }
    } else {
      // For "all" and other tabs, preserve saved list IDs (listId) so items can be added to them
      // But don't include working list IDs
      if (listId) {
        url += `&listId=${listId}`;
      }
    }

    navigate(url);
  };

  const GeneralTabActions = () => {
    const createListProps = {
      ...actionProps,
      onSuccess: () => (
        formik?.resetForm(), navigate(`/invoice?status=selectListWorking`)
      ),
      typeId: 10,
    };

    return <SelectList.CreateList {...createListProps} />;
  };

  const tabProps = {
    active: status,
    onChange: handleChange,
    tabs: [
      // { actions: GeneralTabActions, label: 'All Invoices', name: 'all' },
      {
        actions: GeneralTabActions,
        label: "All Invoices",
        name: "all",
        tableProps: searchedInvoices(),
      },
      {
        actions: SelectListWorking.Actions,
        label: "Select List (Working)",
        name: "selectListWorking",
        tableProps: SelectListWorking.tableProps({ listId, workingListId }),
      },
      {
        actions: SelectListSaved.Actions,
        label: "Select List (Saved)",
        name: "selectListSaved",
        tableProps: SelectListSaved.tableProps({ formik }),
      },
    ],
  };
  const { tableProps: tabTableProps, actions: tabActions } =
    tabProps?.tabs?.find(tab => tab.name === status) || {};

  // function fixes header.name for select lists when status = selectListWorking
  const fixHeadersName = ({ status, headers }) =>
    headers?.map(header => {
      return {
        ...header,
        name:
          header?.name && status === "selectListWorking"
            ? Array.isArray(header?.name)
              ? header?.name?.map(item => `item.${item}`)
              : `item.${header?.name}`
            : header?.name,
      };
    });

  const tableProps = {
    variant: "dynamicHeight",
    dataKey: "invoices",
    headers: fixHeadersName({
      status,
      headers: [
        multiSelect(multiSelectProps),
        { label: "Invoice #", type: "custom", component: InvoiceNumber },
        { label: "Date", name: "date", type: "date" },
        { label: "Invoiced From", name: "company.name", type: "label" },
        { label: "Type", name: "type.description", type: "label" },
        {
          label: "Approved",
          name: "approved",
          type: "custom",
          component: Approved,
        },
        {
          label: "Client Name & Company",
          type: "custom",
          component: props => nameAndCompany(props),
        },
        { label: "Currency", name: "currency.code", type: "label" },
        { label: "Total", name: "invoiceTotals.total", type: "currency" },
        { label: "Balance", name: "invoiceTotals.balance", type: "currency" },
        { label: "Due Date", name: "dueDate", type: "date" },
        { label: "", name: "action", type: "custom", component: Actions },
      ],
    }),
    inputs: [],
    tabProps,
    ...tabTableProps,
    onQueryStateChange: captureNavigationSnapshot,
  };

  return (
    <>
      <HeaderComponent
        leftText="All Invoices"
        rightComponent={() => {
          return <div className="flex gap-4">{tabActions?.(actionProps)}</div>;
        }}
      />
      <div className="mt-6 w-full">
        <Table {...tableProps} />
      </div>
    </>
  );
};

export default List;
